// Google Sign-In DIRECT côté WEB (hors Entra) via le flux OAuth par REDIRECTION
// (authorization code). Fiable partout (y compris navigation privée) — pas de
// One Tap/FedCM. Au clic : redirection vers Google ; au retour sur
// /auth/google/callback : on échange le code contre une session maison (backend).
// Résolu par webpack via l'extension .web.ts.

import { resolveApiBase, getEnvConfig } from '../config/env';
import { decodeJwt } from './entraConfig';
import type { Session } from './entraAuth.web';

const SESSION_KEY = 'ccjaw.session';
const STATE_KEY = 'ccjaw.google.state';
const CALLBACK_PATH = '/auth/google/callback';

function redirectUri(): string {
  return `${window.location.origin}${CALLBACK_PATH}`;
}

function randomState(): string {
  const a = new Uint8Array(24);
  crypto.getRandomValues(a);
  let s = '';
  for (let i = 0; i < a.length; i++) s += String.fromCharCode(a[i]);
  return btoa(s).replace(/[+/=]/g, '');
}

/** Lance la connexion Google : redirige vers Google (la page se recharge). */
export async function loginWithGoogle(): Promise<Session | null> {
  const state = randomState();
  sessionStorage.setItem(STATE_KEY, state);
  const params = new URLSearchParams({
    client_id: getEnvConfig().AUTH_GOOGLE_WEB_CLIENT_ID,
    redirect_uri: redirectUri(),
    response_type: 'code',
    scope: 'openid email profile',
    state,
    prompt: 'select_account',
    access_type: 'online',
  });
  window.location.assign(`https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`);
  return null; // la page redirige : aucune session retournée ici
}

/** Au retour de Google : échange le code -> session backend. Sinon no-op. */
export async function completeGoogleIfCallback(): Promise<Session | null> {
  if (typeof window === 'undefined' || window.location.pathname !== CALLBACK_PATH) return null;
  const qs = new URLSearchParams(window.location.search);
  const code = qs.get('code');
  const state = qs.get('state');
  const err = qs.get('error');
  const saved = sessionStorage.getItem(STATE_KEY);
  sessionStorage.removeItem(STATE_KEY);
  const cleanup = () => window.history.replaceState({}, document.title, '/');

  if (err) {
    cleanup();
    throw new Error(`Google: ${err}`);
  }
  if (!code) {
    cleanup();
    return null;
  }
  if (!state || state !== saved) {
    cleanup();
    throw new Error('état Google invalide');
  }
  const res = await fetch(`${resolveApiBase()}/auth/google`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ code, redirect_uri: redirectUri() }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    cleanup();
    throw new Error(`Connexion Google refusée: ${data?.detail || res.status}`);
  }
  const session: Session & { kind?: string } = {
    accessToken: data.access_token,
    refreshToken: data.refresh_token,
    expiresAt: Math.floor(Date.now() / 1000) + (data.expires_in || 3600),
    claims: decodeJwt(data.access_token),
    kind: 'google',
  };
  sessionStorage.setItem(SESSION_KEY, JSON.stringify(session));
  cleanup();
  return session;
}
